import { IconNames } from '@blueprintjs/icons';
import { type BenefitType } from '@strata/constants';
import { DetailLabel, DetailLabelValuePair } from '@strata/react-label-value-pairs';
import { BaseNotification, type NotificationBaseProps } from '@strata/react-notifications';
import { CountdownTimer } from '@strata/react-timers';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router';

import {
    addQtyUnits,
    getEstDispensedQtyUnits,
    useProductConfig,
} from 'services/ProductConfigHelpers';
import { AppRoutes } from 'services/routes';

export interface EndingSoonNotificationProps extends NotificationBaseProps {
    linkingId: string;
    segmentId: string;
    productServiceOid: string;
    productName: string;
    timestamp: string;
    small: boolean;
    additionalData: {
        auctionEnd: string;
        strength: string;
        qty: number;
        benefit: BenefitType;
    };
}

export const EndingSoonNotification = ({
    linkingId,
    segmentId,
    productServiceOid,
    productName,
    timestamp,
    additionalData,
    onNotificationClick,
    onRemoveNotificationClick,
    status,
    small,
}: EndingSoonNotificationProps) => {
    const navigate = useNavigate();
    const productConfig = useProductConfig({
        segmentId,
        productServiceOid,
        benefit: additionalData?.benefit?.trim() as BenefitType,
    });
    return (
        <BaseNotification
            notificationTitle="Ending Soon"
            icon={IconNames.TAKE_ACTION}
            bodyText={`A ${productName} referral you have bid on is ending soon.`}
            timestamp={timestamp}
            onNotificationClick={() => {
                onNotificationClick();
                navigate(AppRoutes.liveReferralDetails(linkingId));
            }}
            onRemoveNotificationClick={onRemoveNotificationClick}
            type={BaseNotification.Types.CAUTION}
            status={status}
            lapsed={dayjs().isAfter(additionalData.auctionEnd)}
            additionalData={
                <>
                    <div style={{ width: '25%' }}>
                        <DetailLabel label="Time Remaining" />
                        <CountdownTimer endTime={additionalData.auctionEnd} />
                    </div>
                    {!small && (
                        <>
                            <DetailLabelValuePair
                                style={{ width: '25%' }}
                                label="Strength"
                                value={additionalData.strength}
                            />
                            <DetailLabelValuePair
                                style={{ width: '25%' }}
                                label="Estimated Quantity"
                                value={addQtyUnits(
                                    additionalData.qty,
                                    getEstDispensedQtyUnits(productConfig),
                                )}
                            />
                        </>
                    )}
                </>
            }
        />
    );
};
